import React, { useRef } from 'react';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { useMegaMenu } from './MegaMenuContext';
import { hoverGracePeriod, secondaryNavigationId } from './MegaMenu.config';
import useTrackingData, { formatTrackingLabel, } from '@polestar/component-warehouse-react/hooks/useTrackingData';
import { DataLayerAction, DataLayerCategory, DataLayerEvent, } from '@polestar/component-warehouse-react/tokens/analytics/Analytics';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import { labelTextStyle, linkStyle, } from '@polestar/component-warehouse-react/components/mega-menu/internal/MegaMenu.styles';
import Icon from '@polestar/component-warehouse-react/components/icon/Icon';
import { cwRTLTranslate } from '@polestar/component-warehouse-react/components/mega-menu/internal/MegaMenu.config';
import HiddenAccessibilityElement, { visuallyHiddenStyles, } from '@polestar/component-warehouse-react/components/hidden-accessibility-element/HiddenAccessibilityElement';
export const MegaMenuMultiPurposeLink = ({ link: { href, label, external, chevronLabel, eventLabel, ariaLabel }, openExternalLinkText, navigationIndex, megaMenuId, onMouseEnter, onMouseLeave, }) => {
    const hoverTimeoutRef = useRef(null);
    // Tracking
    const { pushDataLayer } = useTrackingData();
    const trackingLinkClick = () => {
        pushDataLayer({
            event: DataLayerEvent.LINK_CLICK,
            category: DataLayerCategory.MENU,
            action: DataLayerAction.CATEGORY_CLICK,
            label: formatTrackingLabel(eventLabel),
        });
    };
    const trackingChevronInteraction = () => {
        pushDataLayer({
            event: DataLayerEvent.LINK_CLICK,
            category: DataLayerCategory.MENU,
            action: DataLayerAction.CATEGORY_CLICK,
            label: formatTrackingLabel(eventLabel),
            'menu.function': 'tab',
        });
    };
    const { setFlyoutActive, setActiveFlyoutIndex, activeFlyoutIndex, setRestoreElement, flyoutItems, setFlyoutHasFocus, setElementAfterFlyout, mainMenuItems, } = useMegaMenu();
    const openFlyout = (index) => {
        if (index === undefined) {
            closeFlyout();
            return;
        }
        setFlyoutActive(true);
        setActiveFlyoutIndex(index);
    };
    const closeFlyout = () => {
        setFlyoutActive(false);
        setActiveFlyoutIndex(null);
    };
    const onChevronClick = (index) => {
        if (index !== activeFlyoutIndex) {
            openFlyout(index);
            trackingChevronInteraction();
        }
        else {
            closeFlyout();
        }
    };
    const onTouchFallBackButtonClick = (index) => {
        index !== activeFlyoutIndex ? openFlyout(index) : closeFlyout();
    };
    const onMenuButtonMouseEnter = (index, event) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        hoverTimeoutRef.current = setTimeout(() => {
            openFlyout(index);
            onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(event);
        }, hoverGracePeriod);
    };
    const onMenuButtonMouseLeave = (event) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
            onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(event);
        }
    };
    const onPanelButtonClick = (index, event) => {
        trackingLinkClick();
        openFlyout(index);
        setRestoreElement(event.currentTarget);
    };
    const onChevronKeyDown = (index, event) => {
        if (event.key === 'Tab' && !event.shiftKey && activeFlyoutIndex === index) {
            event.preventDefault();
            // todo: Check whether `setRestoreElement` and `setElementAfterFlyout` should be set too when the interaction is not done via keyboard but a hybrid of mouse and keyboard
            setRestoreElement(event.currentTarget);
            setElementAfterFlyout(mainMenuItems[mainMenuItems.indexOf(event.currentTarget) + 1]);
            flyoutItems[index][0].focus();
            setFlyoutHasFocus(true);
            return;
        }
        setRestoreElement(null);
    };
    const onMenuButtonLinkKeyDown = (index, event) => {
        handleShiftTab(index, event);
    };
    const handleShiftTab = (index, event) => {
        if (event.key === 'Tab' && event.shiftKey && index === 0) {
            closeFlyout();
        }
        if (event.key === 'Tab' && event.shiftKey && activeFlyoutIndex === index - 1) {
            event.preventDefault();
            setFlyoutHasFocus(true);
            setRestoreElement(mainMenuItems[mainMenuItems.indexOf(event.currentTarget) - 1]);
            setElementAfterFlyout(mainMenuItems[mainMenuItems.indexOf(event.currentTarget)]);
            flyoutItems[index - 1][flyoutItems[index - 1].length - 1].focus();
        }
    };
    const externalLinkAttributes = {
        target: '_blank',
        rel: 'noopener noreferrer',
    };
    return (React.createElement(LinkWrapper, { "data-type": "primary-navigation-item" },
        React.createElement(PanelItemWrapper, null, navigationIndex != undefined ? (React.createElement(PanelButton, { onClick: (event) => onPanelButtonClick(navigationIndex, event), "data-type": "panel-button", ...aria({
                expanded: activeFlyoutIndex === navigationIndex,
                label: ariaLabel,
            }) },
            React.createElement(PanelButtonLabel, null, label),
            React.createElement(PanelButtonChevron, { icon: Icons.chevronRight }))) : (React.createElement(PanelLink, { href: href, ...(external && externalLinkAttributes), onClick: () => trackingLinkClick() },
            React.createElement(PanelButtonLabel, null, label),
            external && !!openExternalLinkText && (React.createElement(React.Fragment, null,
                ' ',
                React.createElement(HiddenNewWindowText, null, openExternalLinkText)))))),
        React.createElement(MenuButton, { onMouseEnter: (event) => onMenuButtonMouseEnter(navigationIndex, event), onMouseLeave: onMenuButtonMouseLeave, "data-type": "link-wrapper" },
            href ? (React.createElement(MenuButtonLink, { "data-type": "link", href: href, onClick: () => trackingLinkClick(), onKeyDown: (event) => onMenuButtonLinkKeyDown(navigationIndex, event), ...(external && externalLinkAttributes) },
                label,
                external && openExternalLinkText && (React.createElement(React.Fragment, null,
                    ' ',
                    React.createElement(HiddenNewWindowText, null, openExternalLinkText))))) : (React.createElement(MenuButtonLabel, null, label)),
            navigationIndex != undefined && (React.createElement(React.Fragment, null,
                React.createElement(TouchFallBackButton, { "data-type": "touch-link", onClick: () => onTouchFallBackButtonClick(navigationIndex), ...aria({
                        expanded: activeFlyoutIndex === navigationIndex,
                        controls: `${megaMenuId}-${secondaryNavigationId}-${navigationIndex}`,
                    }) },
                    React.createElement(HiddenAccessibilityElement, null, chevronLabel)),
                React.createElement(Chevron, { "data-type": "chevron", onClick: () => onChevronClick(navigationIndex), onKeyDown: (event) => {
                        onChevronKeyDown(navigationIndex, event);
                        !href && handleShiftTab(navigationIndex, event);
                    }, ...aria({
                        expanded: activeFlyoutIndex === navigationIndex,
                        controls: `${megaMenuId}-${secondaryNavigationId}-${navigationIndex}`,
                        label: chevronLabel,
                    }) },
                    React.createElement(StyledIcon, { style: {
                            [MultiPurposeLinkCSSCustomProps.ChevronRotation]: activeFlyoutIndex === navigationIndex ? '180deg' : '0deg',
                        }, width: "8", height: "8", viewBox: "0 0 8 8", xmlns: "http://www.w3.org/2000/svg", ...aria({ hidden: true }) },
                        React.createElement("path", { d: "M1.52505 1.88391L4.00004 4.35891L6.47503 1.88391L7.35359 2.76246L4.00004 6.11602L0.646484 2.76246L1.52505 1.88391Z" }))))))));
};
/**
 * There is a tiny spacing required between the visually hidden chevron and the text link
 */
const chevronSpacing = '4px';
var MultiPurposeLinkCSSCustomProps;
(function (MultiPurposeLinkCSSCustomProps) {
    MultiPurposeLinkCSSCustomProps["ChevronRotation"] = "--multi-purpose-link-chevron-rotation";
})(MultiPurposeLinkCSSCustomProps || (MultiPurposeLinkCSSCustomProps = {}));
const buttonResetStyles = css `
  border: none;
  margin: 0;
  padding: 0;
  inline-size: auto;
  overflow: visible;
  color: inherit;
  font: inherit;
  background: inherit;
  -webkit-appearance: none;
  text-align: start;
`;
const panelTextStyles = css `
  overflow: hidden;
  ${linkStyle};
  padding-block: 3px 1px;
`;
const sharedMenuButtonStyles = css `
  ${labelTextStyle};
  padding-block: 5px 1px;
  display: block;
`;
const LinkWrapper = styled.div `
  isolation: isolate;
  position: relative;
  display: flex;
  flex-direction: row;
  align-content: center;
  justify-content: space-between;
  gap: ${Spacings.xxSmall};
  inline-size: 100%;
  cursor: pointer;
`;
const PanelItemWrapper = styled.div `
  inline-size: 100%;

  ${MediaQueries.desktop} {
    display: none;
  }
`;
const PanelButton = styled.button `
  ${buttonResetStyles};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  inline-size: 100%;
  align-items: center;
  cursor: pointer;
  padding: ${createCustomSpacing(3 / 16)} 0;
`;
const PanelLink = styled.a `
  ${panelTextStyles}
`;
const PanelButtonLabel = styled.span `
  ${panelTextStyles}
`;
const PanelButtonChevron = styled(Icon) `
  margin-inline-end: ${createCustomSpacing(8 / 16)};
`;
const MenuButton = styled.div `
  display: none;

  max-width: 20vw;

  span {
    text-align: start;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  ${MediaQueries.desktop} {
    display: block;
  }
`;
const MenuButtonLink = styled.a `
  ${sharedMenuButtonStyles}
`;
const MenuButtonLabel = styled.span `
  ${sharedMenuButtonStyles}
`;
const TouchFallBackButton = styled.button `
  position: absolute;
  inset: 0;
  z-index: 1;
  ${buttonResetStyles};

  [data-interaction-mode='keyboard'] & {
    display: none;
  }

  @media not (pointer: coarse) {
    display: none;
  }
`;
const Chevron = styled.button `
  ${buttonResetStyles};
  position: absolute;
  cursor: pointer;
  inset-block-start: 50%;
  inset-inline-end: 0;
  translate: calc((100% + ${chevronSpacing}) * var(${cwRTLTranslate}, 1)) -50%;
  block-size: 100%;

  &:not(:focus) {
    ${visuallyHiddenStyles};
  }
`;
const StyledIcon = styled.svg `
  rotate: var(${MultiPurposeLinkCSSCustomProps.ChevronRotation}, 0deg);
`;
const HiddenNewWindowText = styled.span `
  ${visuallyHiddenStyles};
`;
